import { useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { GeoJSON, Marker, Pane, Polyline, Tooltip } from "react-leaflet";
import bezierSpline from "@turf/bezier-spline";
import { lineString } from "@turf/helpers";
import { divIcon, DomEvent } from "leaflet";
import hash from "object-hash";

import { Path, Inventory } from "Types/inventory";

import { Card, Stack, Typography } from "@mui/material";

import useEditPath from "Hooks/useEditPath";
import usePaths from "Hooks/usePaths";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";

import { changeModal } from "Redux/modalSlice";
import { changeLargeIconByClick } from "Redux/mapSlice";
import { setTooltip } from "Redux/tooltipSlice";
import { selectStationStar } from "Redux/stationsSlice";

import HintBox from "Components/HintBox";
import { Box } from "@mui/system";
import { invData } from "Pages/Home/side/rightSide2/inventoryData";
import Icon from "Components/Icon";
import zIndex from "@mui/material/styles/zIndex";

const ICON_SIZE = 22;
export default function ArcAndShadow2() {
  const { visible } = useAppSelector((state) => state.inventory);

  const dispatch = useAppDispatch();
  const { editPath } = useEditPath();

  const [showHint, setShowHint] = useState<boolean>(false);
  const [invPathes, setInvPathes] = useState<Inventory[]>([]);

  useEffect(() => {
    const codes: any = [];
    const pathContainer: Inventory[] = [];
    for (let i = 0; i < invData.length; i += 1) {
      if (!codes.includes(invData[i].path.code)) {
        codes.push(invData[i].path.code);
        pathContainer.push(invData[i]);
      }
    }
    setInvPathes(pathContainer);
  }, []);

  const curvePolyline = useMemo(() => {
    if (!visible) return;
    return invPathes?.map((item: Inventory) => {
      if (!item?.path) return;
      const stations = item?.path?.stations;
      const sourceStation = stations[0];
      const destinationStation = stations[stations.length - 1];

      const serviceColor = "#00C19F";
      const stationData = {
        type: "behamrah",
        text: "بهمراهی",
        icon: <Icon img="/assets/behamrah-logo.svg" imgProps={{ style: { height: "22px", zIndex: "2" } }} />,
      };
      const sourceIconMarkup = renderToStaticMarkup(
        <Box
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: "#fff",
            borderRadius: "50%",
            border: "2px solid",
            display: "grid",
            placeItems: "center",
            borderColor: serviceColor,
          }}
        >
          {stationData.icon}
        </Box>
      );

      const destinationIconMarkup = renderToStaticMarkup(
        <Box
          style={{
            width: "45px",
            height: "50px",
            backgroundColor: "#ffffff00",
            borderRadius: "none",
            display: "grid",
            placeItems: "center",
            borderColor: serviceColor,
            marginLeft: "6px",
            marginTop: "2px",
          }}
        >
          <svg width={55} height={50} viewBox="3 2 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7.96891 4.24999C9.31606 1.91666 12.6839 1.91666 14.0311 4.25L18.3612 11.75C19.7084 14.0833 18.0244 17 15.3301 17H6.66986C3.97557 17 2.29163 14.0833 3.63878 11.75L7.96891 4.24999Z"
              fill="#fff"
              stroke={serviceColor}
              strokeWidth="1"
              strokeLinejoin="round"
            />
          </svg>
          {stationData.icon}
        </Box>
      );

      const sourceIcon = divIcon({
        html: sourceIconMarkup,
        iconSize: [ICON_SIZE, ICON_SIZE],
        iconAnchor: [18, 30],
      });
      const destinationIcon = divIcon({
        html: destinationIconMarkup,
        iconSize: [ICON_SIZE, ICON_SIZE],
        iconAnchor: [18, 30],
      });

      const finalResult = [];

      const latLng1 = [sourceStation.lat, sourceStation.lng];
      const latLng2 = [destinationStation.lat, destinationStation.lng];

      const offsetX = latLng2[1] - latLng1[1];
      const offsetY = latLng2[0] - latLng1[0];

      const r1 = Math.sqrt(offsetX ** 2 + offsetY ** 2);
      const theta = Math.atan2(offsetY, offsetX);

      const thetaOffset = 3.14 / 20;

      const r2 = r1 / 2 / Math.cos(thetaOffset);
      const theta2 = theta + thetaOffset;

      const midpointX = r2 * Math.cos(theta2) + latLng1[1];
      const midpointY = r2 * Math.sin(theta2) + latLng1[0];

      const midpointLatLng = [midpointY, midpointX];

      finalResult.push(latLng1, midpointLatLng, latLng2);

      const line = lineString(finalResult.map((latLng) => [latLng[1], latLng[0]]));
      const curved = bezierSpline(line, { sharpness: 1, resolution: 10000 });

      return (
        <>
          <>
            <Marker position={[sourceStation.lat, sourceStation.lng]} icon={sourceIcon} zIndexOffset={1000} />
            <Marker position={[destinationStation.lat, destinationStation.lng]} icon={destinationIcon} zIndexOffset={1000} />
          </>
          <Polyline
            positions={[
              [sourceStation.lat, sourceStation.lng],
              [destinationStation.lat, destinationStation.lng],
            ]}
            pathOptions={{ opacity: 0.2, color: "#000", weight: 5 }}
          />
          <GeoJSON data={curved} pathOptions={{ weight: 5, color: serviceColor }} key={hash(curved)} />
        </>
      );
    });
  }, [invPathes, visible]);

  return (
    <>
      {/* <Pane name="path-arc" style={{ zIndex: pathLayer }} key={pathLayer}>
        {curvePolyline}
      </Pane> */}
      {curvePolyline}
    </>
  );
}
